import { useNavigate } from "react-router-dom";
import { ChevronLeft, ShieldCheck, HeartHandshake } from "lucide-react";

/**
 * FitFrik Community Rules — public, static, accessible from the shield
 * icon in the Community header, the Report bottom sheet, and Settings.
 *
 * Style: matches existing app tokens — Barlow Condensed headings,
 * Barlow body, warm grey canvas, no shadows.
 */
export default function CommunityRules() {
  const nav = useNavigate();
  return (
    <div
      data-testid="community-rules-page"
      className="min-h-screen"
      style={{ background: "var(--canvas, #F4F3F1)" }}
    >
      {/* Header */}
      <div
        className="sticky top-0 z-10 flex items-center gap-3 px-4 h-14 border-b"
        style={{ background: "var(--canvas, #F4F3F1)", borderColor: "var(--border, rgba(12,12,11,0.08))" }}
      >
        <button
          onClick={() => nav(-1)}
          data-testid="rules-back"
          className="w-10 h-10 grid place-items-center rounded-full"
          aria-label="Back"
        >
          <ChevronLeft size={22} />
        </button>
        <h1
          style={{
            fontFamily: "Barlow Condensed, sans-serif",
            fontWeight: 700,
            fontSize: 22,
            letterSpacing: "0.02em",
            color: "var(--text-primary)",
          }}
        >
          Community Rules
        </h1>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        {/* Hero */}
        <section className="flex items-start gap-3">
          <ShieldCheck size={22} style={{ color: "#00D4FF", marginTop: 2 }} />
          <p
            style={{
              fontFamily: "Barlow",
              fontSize: 14,
              lineHeight: 1.55,
              color: "var(--text-secondary, #4A4845)",
            }}
          >
            FitFrik is a community of people showing up for their fitness. These
            rules keep it safe and welcoming for everyone. Breaking them can
            lead to your content being removed or your account being restricted.
          </p>
        </section>

        {/* DO */}
        <Section title="Do">
          <Bullet>Share your real workouts and progress.</Bullet>
          <Bullet>Support people who are starting out.</Bullet>
          <Bullet>Ask questions — no question is too basic.</Bullet>
          <Bullet>Give credit when you share someone&apos;s tip.</Bullet>
          <Bullet>Report anything that feels wrong.</Bullet>
        </Section>

        {/* DON'T */}
        <Section title="Don&apos;t">
          <Bullet>
            Share phone numbers, WhatsApp handles, or any other contact
            details. Bookings stay on FitFrik for your protection.
          </Bullet>
          <Bullet>
            Sell products or services outside the FitFrik marketplace.
          </Bullet>
          <Bullet>Post medical claims or guarantee specific results.</Bullet>
          <Bullet>Body-shame anyone, including yourself.</Bullet>
          <Bullet>Post content that involves minors.</Bullet>
          <Bullet>Impersonate a trainer or a brand.</Bullet>
        </Section>

        {/* 3-STEP ENFORCEMENT */}
        <Section title="How enforcement works">
          <Step n={1} title="Warning">
            The content is removed and you are told why.
          </Step>
          <Step n={2} title="7-day restriction">
            You can read the community but not post.
          </Step>
          <Step n={3} title="Permanent removal from the community">
            Your account and personal data remain. You lose posting and
            commenting privileges permanently.
          </Step>
        </Section>

        {/* REPORTING */}
        <Section title="How reporting works">
          <p style={paragraphStyle}>
            Tap the three-dot menu on any post, tip or comment. Choose a
            reason. Our team reviews reports within 24 hours. The person you
            report is never told who reported them.
          </p>
        </Section>

        {/* SELF-HARM SUPPORT */}
        <section
          className="rounded-[14px] p-4 border"
          style={{ background: "#FFF7F5", borderColor: "#F5C6BC" }}
        >
          <div className="flex items-start gap-2">
            <HeartHandshake size={18} style={{ color: "#7C2D12", marginTop: 2 }} />
            <div>
              <h3
                style={{
                  fontFamily: "Barlow Condensed, sans-serif",
                  fontWeight: 700,
                  fontSize: 17,
                  color: "#7C2D12",
                }}
              >
                A note on eating and self-harm content
              </h3>
              <p
                style={{
                  fontFamily: "Barlow",
                  fontSize: 13,
                  color: "#7C2D12",
                  lineHeight: 1.55,
                  marginTop: 6,
                }}
              >
                If you are struggling, reporting is not the only option — please
                reach out. In India, you can call{" "}
                <strong>AASRA at 91-9820466726</strong> (24×7) or{" "}
                <strong>Tele-MANAS at 14416</strong> (toll-free). You deserve
                support.
              </p>
            </div>
          </div>
        </section>

        <div style={{ height: 40 }} />
      </div>
    </div>
  );
}

const paragraphStyle = {
  fontFamily: "Barlow",
  fontSize: 14,
  color: "var(--text-secondary, #4A4845)",
  lineHeight: 1.55,
};

function Section({ title, children }) {
  return (
    <section>
      <h2
        style={{
          fontFamily: "Barlow Condensed, sans-serif",
          fontWeight: 700,
          fontSize: 20,
          color: "var(--text-primary)",
          letterSpacing: "0.02em",
          marginBottom: 10,
        }}
        dangerouslySetInnerHTML={{ __html: title }}
      />
      <div className="space-y-2">{children}</div>
    </section>
  );
}

function Bullet({ children }) {
  return (
    <div className="flex gap-2 items-start">
      <div
        style={{
          width: 6,
          height: 6,
          borderRadius: 999,
          background: "#00D4FF",
          marginTop: 8,
          flexShrink: 0,
        }}
      />
      <p style={paragraphStyle}>{children}</p>
    </div>
  );
}

function Step({ n, title, children }) {
  return (
    <div className="flex gap-3 items-start">
      <div
        style={{
          width: 26,
          height: 26,
          borderRadius: 999,
          background: "#0C0C0B",
          color: "#F4F3F1",
          fontFamily: "Barlow Condensed, sans-serif",
          fontWeight: 700,
          fontSize: 14,
          display: "grid",
          placeItems: "center",
          flexShrink: 0,
        }}
      >
        {n}
      </div>
      <div>
        <div
          style={{
            fontFamily: "Barlow Condensed, sans-serif",
            fontWeight: 700,
            fontSize: 15,
            color: "var(--text-primary)",
          }}
        >
          {title}
        </div>
        <p style={{ ...paragraphStyle, marginTop: 2 }}>{children}</p>
      </div>
    </div>
  );
}
